import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { usePageSettings } from '../context/PageSettingsContext';
import { supabase, supabaseAdmin } from '../supabaseClient';
import Toast, { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import './Admin.css';

// NOTE: supabaseAdmin uses the service role key which is bundled in this
// client-side build. This is acceptable only for an internal-only admin
// route protected by role-based auth. For a public app, proxy admin
// operations through a backend/edge function instead.

const BLANK_JOB       = { title: '', location: '', type: '', experience: '', description: '' };
const BLANK_BLOG      = { title: '', author: '', content: '', date: '' };
const BLANK_CLIENT    = { name: '', logo: '' };
const BLANK_CS        = { title: '', tag: '', problem: '', solution: '', technology: '',
  result1Number: '', result1Label: '', result2Number: '', result2Label: '',
  result3Number: '', result3Label: '', result4Number: '', result4Label: '' };

function Admin() {
  usePageTitle('Admin');
  const [activeTab, setActiveTab] = useState('contacts');
  const { signOut } = useAuth();
  const navigate    = useNavigate();
  const { toast, showToast, clearToast } = useToast();

  const [contacts,        setContacts]        = useState([]);
  const [jobs,            setJobs]            = useState([]);
  const [blogs,           setBlogs]           = useState([]);
  const [caseStudies,     setCaseStudies]      = useState([]);
  const [clients,         setClients]         = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [privacyPolicy,   setPrivacyPolicy]   = useState('');
  const [termsOfService,  setTermsOfService]  = useState('');
  const [users,           setUsers]           = useState([]);

  const [userForm,    setUserForm]    = useState({ email: '', password: '', role: 'internal_user' });
  const [jobForm,     setJobForm]     = useState(BLANK_JOB);
  const [editingJobId,setEditingJobId]= useState(null);
  const [blogForm,    setBlogForm]    = useState(BLANK_BLOG);
  const [clientForm,  setClientForm]  = useState(BLANK_CLIENT);
  const [caseForm,    setCaseForm]    = useState(BLANK_CS);

  // Confirm dialog state
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', onConfirm: null });
  const openConfirm = (title, message, onConfirm) =>
    setConfirm({ open: true, title, message, onConfirm });
  const closeConfirm = () =>
    setConfirm(c => ({ ...c, open: false }));

  const { pages, setPages } = usePageSettings();

  const loadAll = useCallback(async () => {
    try {
      const [c, j, b, cs, cl, ja] = await Promise.all([
        supabase.from('contacts').select('*').order('created_at', { ascending: false }),
        supabase.from('jobs').select('*').order('created_at', { ascending: false }),
        supabase.from('blogs').select('*').order('created_at', { ascending: false }),
        supabase.from('case_studies').select('*').order('created_at', { ascending: false }),
        supabase.from('clients').select('*').order('created_at', { ascending: false }),
        supabase.from('job_applications').select('*').order('created_at', { ascending: false }),
      ]);
      if (c.data)  setContacts(c.data);
      if (j.data)  setJobs(j.data);
      if (b.data)  setBlogs(b.data);
      if (cs.data) setCaseStudies(cs.data);
      if (cl.data) setClients(cl.data);
      if (ja.data) setJobApplications(ja.data);

      const pp  = await supabase.from('site_content').select('content').eq('id', 'privacy_policy').single();
      const tos = await supabase.from('site_content').select('content').eq('id', 'terms_of_service').single();
      if (pp.data?.content)  setPrivacyPolicy(pp.data.content);
      if (tos.data?.content) setTermsOfService(tos.data.content);
    } catch {
      showToast('Failed to load data. Please refresh.', 'error');
    }
  }, [showToast]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data, error } = await supabaseAdmin.auth.admin.listUsers();
      if (!error) setUsers(data.users);
    } catch {
      // service key unavailable — silent fail for users tab
    }
  }, []);

  useEffect(() => {
    loadAll();
    fetchUsers();
  }, [loadAll, fetchUsers]);

  // ── Page toggle ────────────────────────────────────────────────────────────
  const PAGE_LIST = [
    { id: 'home',         label: 'Home' },
    { id: 'services',     label: 'Services' },
    { id: 'ai-solutions', label: 'AI Solutions' },
    { id: 'staffing',     label: 'Staffing & Teams' },
    { id: 'industries',   label: 'Industries' },
    { id: 'case-studies', label: 'Case Studies' },
    { id: 'about',        label: 'About Us' },
    { id: 'blog',         label: 'Blog' },
    { id: 'careers',      label: 'Careers' },
    { id: 'contact',      label: 'Contact' },
    { id: 'privacy',      label: 'Privacy Policy' },
    { id: 'terms',        label: 'Terms of Service' },
  ];

  const togglePage = async (pageId, current) => {
    const newVal = !current;
    const { error } = await supabase.from('page_settings').update({ enabled: newVal }).eq('id', pageId);
    if (!error) setPages(prev => ({ ...prev, [pageId]: newVal }));
    else showToast('Failed to update page setting.', 'error');
  };

  // ── User management ────────────────────────────────────────────────────────
  const addUser = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabaseAdmin.auth.admin.createUser({
        email: userForm.email, password: userForm.password,
        email_confirm: true, user_metadata: { role: userForm.role },
      });
      if (error) { showToast(error.message, 'error'); return; }
      showToast(`User ${userForm.email} created.`, 'success');
      setUserForm({ email: '', password: '', role: 'internal_user' });
      fetchUsers();
    } catch (err) {
      showToast(err.message || 'Failed to create user.', 'error');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    const { error } = await supabaseAdmin.auth.admin.updateUserById(userId, {
      user_metadata: { role: newRole },
    });
    if (!error) fetchUsers();
    else showToast('Failed to update role.', 'error');
  };

  const deleteUser = (userId, email) => {
    openConfirm(
      'Delete User',
      `Delete "${email}"? This cannot be undone.`,
      async () => {
        closeConfirm();
        const { error } = await supabaseAdmin.auth.admin.deleteUser(userId);
        if (!error) { showToast('User deleted.', 'success'); fetchUsers(); }
        else showToast('Failed to delete user.', 'error');
      }
    );
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // ── Jobs ───────────────────────────────────────────────────────────────────
  const addJob = async (e) => {
    e.preventDefault();
    try {
      if (editingJobId) {
        const { error } = await supabase.from('jobs').update(jobForm).eq('id', editingJobId);
        if (error) throw error;
        showToast('Job updated.', 'success');
        setEditingJobId(null);
      } else {
        const { error } = await supabase.from('jobs').insert([jobForm]);
        if (error) throw error;
        showToast('Job posted.', 'success');
      }
      setJobForm(BLANK_JOB);
      loadAll();
    } catch (err) {
      showToast(err.message || 'Failed to save job.', 'error');
    }
  };

  const editJob = (job) => {
    setJobForm({ title: job.title, location: job.location, type: job.type, experience: job.experience, description: job.description });
    setEditingJobId(job.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Blogs ──────────────────────────────────────────────────────────────────
  const addBlog = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.from('blogs').insert([blogForm]);
      if (error) throw error;
      showToast('Blog post published.', 'success');
      setBlogForm(BLANK_BLOG);
      loadAll();
    } catch (err) {
      showToast(err.message || 'Failed to save blog post.', 'error');
    }
  };

  // ── Case Studies ───────────────────────────────────────────────────────────
  const addCaseStudy = async (e) => {
    e.preventDefault();
    try {
      const results = [
        { number: caseForm.result1Number, label: caseForm.result1Label },
        { number: caseForm.result2Number, label: caseForm.result2Label },
        { number: caseForm.result3Number, label: caseForm.result3Label },
        { number: caseForm.result4Number, label: caseForm.result4Label },
      ].filter(r => r.number && r.label);

      const { error } = await supabase.from('case_studies').insert([{
        title: caseForm.title, tag: caseForm.tag,
        problem: caseForm.problem, solution: caseForm.solution,
        technology: caseForm.technology, results,
      }]);
      if (error) throw error;
      showToast('Case study added.', 'success');
      setCaseForm(BLANK_CS);
      loadAll();
    } catch (err) {
      showToast(err.message || 'Failed to save case study.', 'error');
    }
  };

  // ── Clients ────────────────────────────────────────────────────────────────
  const addClient = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.from('clients').insert([clientForm]);
      if (error) throw error;
      showToast('Client added.', 'success');
      setClientForm(BLANK_CLIENT);
      loadAll();
    } catch (err) {
      showToast(err.message || 'Failed to add client.', 'error');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setClientForm(f => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  // ── Site content ───────────────────────────────────────────────────────────
  const saveSiteContent = async (id, content, label) => {
    try {
      const { error } = await supabase.from('site_content').update({ content }).eq('id', id);
      if (error) throw error;
      showToast(`${label} saved.`, 'success');
    } catch (err) {
      showToast(err.message || `Failed to save ${label}.`, 'error');
    }
  };

  // ── Delete item ────────────────────────────────────────────────────────────
  const deleteItem = (type, id, label) => {
    const tableMap = { job: 'jobs', blog: 'blogs', case: 'case_studies', contact: 'contacts', client: 'clients' };
    openConfirm(
      'Confirm Delete',
      label ? `Delete "${label}"?` : 'Delete this item? This cannot be undone.',
      async () => {
        closeConfirm();
        try {
          const { error } = await supabase.from(tableMap[type]).delete().eq('id', id);
          if (error) throw error;
          showToast('Deleted.', 'success');
          loadAll();
        } catch (err) {
          showToast(err.message || 'Delete failed.', 'error');
        }
      }
    );
  };

  return (
    <div className="admin">
      <Toast {...toast} onClose={clearToast} />
      <ConfirmDialog
        isOpen={confirm.open}
        title={confirm.title}
        message={confirm.message}
        confirmLabel="Delete"
        danger
        onConfirm={confirm.onConfirm}
        onCancel={closeConfirm}
      />

      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout}>Logout</button>
      </div>

      <div className="admin-tabs">
        {[
          { key: 'contacts', label: `Contact Requests (${contacts.length})` },
          { key: 'jobs',     label: `Job Postings (${jobs.length})` },
          { key: 'blogs',    label: `Blog Posts (${blogs.length})` },
          { key: 'cases',    label: `Case Studies (${caseStudies.length})` },
          { key: 'clients',  label: `Clients (${clients.length})` },
          { key: 'privacy',  label: 'Privacy Policy' },
          { key: 'terms',    label: 'Terms of Service' },
          { key: 'users',    label: `Users (${users.length})`, onClick: fetchUsers },
          { key: 'pages',    label: 'Page Control' },
        ].map(t => (
          <button
            key={t.key}
            className={activeTab === t.key ? 'active' : ''}
            onClick={() => { setActiveTab(t.key); t.onClick?.(); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-content">

        {/* ── Contacts ── */}
        {activeTab === 'contacts' && (
          <div className="admin-section">
            <h2>Contact Requests</h2>
            {contacts.length === 0 ? (
              <p className="empty-state">No contact requests yet</p>
            ) : (
              <div className="data-table">
                {contacts.map(c => (
                  <div key={c.id} className="data-card">
                    <div className="card-header">
                      <h3>{c.name}</h3>
                      <button onClick={() => deleteItem('contact', c.id, c.name)}>Delete</button>
                    </div>
                    <p><strong>Email:</strong> {c.email}</p>
                    <p><strong>Company:</strong> {c.company}</p>
                    <p><strong>Type:</strong> {c.inquiry_type || c.inquiryType}</p>
                    <p><strong>Message:</strong> {c.message}</p>
                    <p className="timestamp">{new Date(c.created_at || c.timestamp).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Jobs ── */}
        {activeTab === 'jobs' && (
          <div className="admin-section">
            <h2>Job Postings</h2>
            <form className="admin-form" onSubmit={addJob}>
              <input type="text" placeholder="Job Title" value={jobForm.title}
                onChange={e => setJobForm(f => ({ ...f, title: e.target.value }))} required />
              <input type="text" placeholder="Location" value={jobForm.location}
                onChange={e => setJobForm(f => ({ ...f, location: e.target.value }))} required />
              <input type="text" placeholder="Job Type (Full-time, Part-time…)" value={jobForm.type}
                onChange={e => setJobForm(f => ({ ...f, type: e.target.value }))} required />
              <input type="text" placeholder="Experience (e.g., 2–5 years)" value={jobForm.experience}
                onChange={e => setJobForm(f => ({ ...f, experience: e.target.value }))} required />
              <textarea placeholder="Job Description" value={jobForm.description}
                onChange={e => setJobForm(f => ({ ...f, description: e.target.value }))} rows="4" required />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button type="submit">{editingJobId ? 'Update Job' : 'Add Job'}</button>
                {editingJobId && (
                  <button type="button" onClick={() => { setEditingJobId(null); setJobForm(BLANK_JOB); }}
                    style={{ background: '#64748b' }}>Cancel Edit</button>
                )}
              </div>
            </form>

            <div className="data-table">
              {jobs.map(job => {
                const applications = jobApplications.filter(a => a.job_id === job.id);
                return (
                  <div key={job.id} className="data-card">
                    <div className="card-header">
                      <h3>{job.title}</h3>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => editJob(job)} style={{ background: '#3b82f6' }}>Edit</button>
                        <button onClick={() => deleteItem('job', job.id, job.title)}>Delete</button>
                      </div>
                    </div>
                    <p><strong>Location:</strong> {job.location}</p>
                    <p><strong>Type:</strong> {job.type}</p>
                    <p><strong>Experience:</strong> {job.experience}</p>
                    <p>{job.description}</p>
                    {applications.length > 0 && (
                      <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#1e293b', borderRadius: '8px' }}>
                        <strong style={{ color: '#60a5fa' }}>Applications ({applications.length}):</strong>
                        {applications.map(app => (
                          <div key={app.id} style={{ marginTop: '1rem', padding: '1rem', background: '#0f172a', borderRadius: '6px' }}>
                            <p><strong>Name:</strong> {app.name}</p>
                            <p><strong>Email:</strong> {app.email}</p>
                            <p><strong>Phone:</strong> {app.phone}</p>
                            <p><strong>Current CTC:</strong> {app.current_ctc}</p>
                            <p><strong>Expected CTC:</strong> {app.expected_ctc}</p>
                            <p><strong>Notice Period:</strong> {app.notice_period}</p>
                            {app.resume_url
                              ? <p><strong>Resume:</strong> <a href={app.resume_url} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>{app.resume_file || 'Download'}</a></p>
                              : <p style={{ color: '#64748b' }}>No resume attached</p>
                            }
                            <p style={{ fontSize: '0.75rem', color: '#64748b' }}>Applied: {new Date(app.created_at).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Blogs ── */}
        {activeTab === 'blogs' && (
          <div className="admin-section">
            <h2>Blog Posts</h2>
            <form className="admin-form" onSubmit={addBlog}>
              <input type="text" placeholder="Blog Title" value={blogForm.title}
                onChange={e => setBlogForm(f => ({ ...f, title: e.target.value }))} required />
              <input type="text" placeholder="Author" value={blogForm.author}
                onChange={e => setBlogForm(f => ({ ...f, author: e.target.value }))} required />
              <input type="date" value={blogForm.date}
                onChange={e => setBlogForm(f => ({ ...f, date: e.target.value }))} required />
              <textarea placeholder="Blog Content" value={blogForm.content}
                onChange={e => setBlogForm(f => ({ ...f, content: e.target.value }))} rows="6" required />
              <button type="submit">Publish Blog Post</button>
            </form>
            <div className="data-table">
              {blogs.map(blog => (
                <div key={blog.id} className="data-card">
                  <div className="card-header">
                    <h3>{blog.title}</h3>
                    <button onClick={() => deleteItem('blog', blog.id, blog.title)}>Delete</button>
                  </div>
                  <p><strong>Author:</strong> {blog.author}</p>
                  <p><strong>Date:</strong> {blog.date}</p>
                  <p>{blog.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Case Studies ── */}
        {activeTab === 'cases' && (
          <div className="admin-section">
            <h2>Case Studies</h2>
            <form className="admin-form" onSubmit={addCaseStudy}>
              <input type="text" placeholder="Title" value={caseForm.title}
                onChange={e => setCaseForm(f => ({ ...f, title: e.target.value }))} required />
              <input type="text" placeholder="Tag (e.g., Computer Vision)" value={caseForm.tag}
                onChange={e => setCaseForm(f => ({ ...f, tag: e.target.value }))} required />
              <textarea placeholder="Problem Description" value={caseForm.problem}
                onChange={e => setCaseForm(f => ({ ...f, problem: e.target.value }))} rows="3" required />
              <textarea placeholder="Solution Description" value={caseForm.solution}
                onChange={e => setCaseForm(f => ({ ...f, solution: e.target.value }))} rows="3" required />
              <input type="text" placeholder="Technology Stack" value={caseForm.technology}
                onChange={e => setCaseForm(f => ({ ...f, technology: e.target.value }))} required />
              {[1,2,3,4].map(n => (
                <div key={n} style={{ padding: '1rem', background: '#1e293b', borderRadius: '8px' }}>
                  <h4 style={{ color: '#60a5fa', marginBottom: '0.75rem' }}>Result {n}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                    <input type="text" placeholder="Number (e.g., 95%)"
                      value={caseForm[`result${n}Number`]}
                      onChange={e => setCaseForm(f => ({ ...f, [`result${n}Number`]: e.target.value }))}
                      required={n <= 2} />
                    <input type="text" placeholder="Label"
                      value={caseForm[`result${n}Label`]}
                      onChange={e => setCaseForm(f => ({ ...f, [`result${n}Label`]: e.target.value }))}
                      required={n <= 2} />
                  </div>
                </div>
              ))}
              <button type="submit">Add Case Study</button>
            </form>
            <div className="data-table">
              {caseStudies.map(cs => (
                <div key={cs.id} className="data-card">
                  <div className="card-header">
                    <h3>{cs.title}</h3>
                    <button onClick={() => deleteItem('case', cs.id, cs.title)}>Delete</button>
                  </div>
                  <p><strong>Tag:</strong> {cs.tag}</p>
                  <p><strong>Problem:</strong> {cs.problem}</p>
                  <p><strong>Solution:</strong> {cs.solution}</p>
                  <p><strong>Technology:</strong> {cs.technology}</p>
                  {Array.isArray(cs.results) && cs.results.length > 0 && (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#1e293b', borderRadius: '8px' }}>
                      <strong style={{ color: '#60a5fa' }}>Results:</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.4rem', marginTop: '0.5rem' }}>
                        {cs.results.map((r, i) => (
                          <span key={i} style={{ color: '#94a3b8', fontSize: '0.875rem' }}>{r.number} — {r.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Clients ── */}
        {activeTab === 'clients' && (
          <div className="admin-section">
            <h2>Client Logos</h2>
            <form className="admin-form" onSubmit={addClient}>
              <input type="text" placeholder="Client Name" value={clientForm.name}
                onChange={e => setClientForm(f => ({ ...f, name: e.target.value }))} required />
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e2e8f0' }}>Upload Logo:</label>
                <input type="file" accept="image/*" onChange={handleLogoUpload}
                  required={!clientForm.logo}
                  style={{ padding: '0.75rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0' }} />
                {clientForm.logo && (
                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ color: '#60a5fa', marginBottom: '0.5rem' }}>Preview:</p>
                    <img src={clientForm.logo} alt="Logo preview" style={{ width: 150, height: 150, objectFit: 'contain', background: 'white', padding: '1rem', borderRadius: '8px' }} />
                  </div>
                )}
              </div>
              <button type="submit">Add Client</button>
            </form>
            <div className="data-table">
              {clients.map(client => (
                <div key={client.id} className="data-card">
                  <div className="card-header">
                    <h3>{client.name}</h3>
                    <button onClick={() => deleteItem('client', client.id, client.name)}>Delete</button>
                  </div>
                  <img src={client.logo} alt={`${client.name} logo`}
                    style={{ width: 150, height: 150, objectFit: 'contain', background: 'white', padding: '1rem', borderRadius: '8px', marginTop: '1rem' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Privacy Policy ── */}
        {activeTab === 'privacy' && (
          <div className="admin-section">
            <h2>Privacy Policy</h2>
            <div className="admin-form">
              <textarea placeholder="Enter Privacy Policy content…" value={privacyPolicy}
                onChange={e => setPrivacyPolicy(e.target.value)} rows="20" style={{ minHeight: 400 }} />
              <button type="button" onClick={() => saveSiteContent('privacy_policy', privacyPolicy, 'Privacy Policy')}>
                Save Privacy Policy
              </button>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '1rem' }}>
                Tip: Use **text** for headings. They will be formatted automatically.
              </p>
            </div>
          </div>
        )}

        {/* ── Terms of Service ── */}
        {activeTab === 'terms' && (
          <div className="admin-section">
            <h2>Terms of Service</h2>
            <div className="admin-form">
              <textarea placeholder="Enter Terms of Service content…" value={termsOfService}
                onChange={e => setTermsOfService(e.target.value)} rows="20" style={{ minHeight: 400 }} />
              <button type="button" onClick={() => saveSiteContent('terms_of_service', termsOfService, 'Terms of Service')}>
                Save Terms of Service
              </button>
              <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '1rem' }}>
                Tip: Use **text** for headings. They will be formatted automatically.
              </p>
            </div>
          </div>
        )}

        {/* ── Users ── */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <h2>Manage Users</h2>
            <form className="admin-form" onSubmit={addUser}>
              <input type="email" placeholder="Email address" value={userForm.email}
                onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} required />
              <input type="password" placeholder="Password (min 6 characters)" value={userForm.password}
                onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} minLength={6} required />
              <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}
                style={{ padding: '0.75rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0', fontSize: '0.95rem' }}>
                <option value="admin">Admin</option>
                <option value="internal_user">Internal User</option>
                <option value="external_user">External User</option>
              </select>
              <button type="submit">Create User</button>
            </form>
            <div className="data-table">
              {users.map(u => (
                <div key={u.id} className="data-card">
                  <div className="card-header">
                    <div>
                      <h3>{u.email}</h3>
                      <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                        Created: {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button onClick={() => deleteUser(u.id, u.email)} style={{ background: '#dc2626' }}>Delete</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Role:</span>
                    <select value={u.user_metadata?.role || ''} onChange={e => updateUserRole(u.id, e.target.value)}
                      style={{ padding: '0.4rem 0.75rem', background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', fontSize: '0.875rem' }}>
                      <option value="">— no role —</option>
                      <option value="admin">Admin</option>
                      <option value="internal_user">Internal User</option>
                      <option value="external_user">External User</option>
                    </select>
                    <span style={{
                      padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                      background: u.user_metadata?.role === 'admin' ? 'rgba(255,107,107,0.2)' : u.user_metadata?.role === 'internal_user' ? 'rgba(102,126,234,0.2)' : 'rgba(78,205,196,0.2)',
                      color: u.user_metadata?.role === 'admin' ? '#ff6b6b' : u.user_metadata?.role === 'internal_user' ? '#667eea' : '#4ecdc4',
                    }}>
                      {u.user_metadata?.role || 'none'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Page Control ── */}
        {activeTab === 'pages' && (
          <div className="admin-section">
            <h2>Page Control</h2>
            <p style={{ color: '#888', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Toggle pages on/off. Disabled pages are hidden from the navbar.
            </p>
            <div className="data-table">
              {PAGE_LIST.map(page => (
                <div key={page.id} className="data-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1rem' }}>{page.label}</h3>
                    <span style={{ color: '#555', fontSize: '0.8rem' }}>/{page.id}</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={pages[page.id] !== false}
                      onChange={() => togglePage(page.id, pages[page.id] !== false)} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Admin;
